import { useState, useRef, useEffect } from 'react'

interface PageSizeSelectProps {
  value: number
  onChange: (value: number) => void
  options?: number[]
  label?: string
}

// Componente custom acessível para seleção de page size
export const PageSizeSelect = ({ value, onChange, options = [10, 20, 30, 50, 75, 100], label = 'Itens' }: PageSizeSelectProps) => {
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(options.indexOf(value) || 0)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<HTMLUListElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [listWidth, setListWidth] = useState<number | null>(null)
  const listboxId = 'page-size-listbox'

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (open && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  // Sincroniza activeIndex com value externo
  useEffect(() => {
    const idx = options.indexOf(value)
    if (idx >= 0) setActiveIndex(idx)
  }, [value, options])

  // Ajusta largura da lista ao abrir
  useEffect(() => {
    if (open && buttonRef.current) {
      setListWidth(buttonRef.current.offsetWidth)
    }
  }, [open])

  // Recalcula largura em resize enquanto aberto
  useEffect(() => {
    const handleResize = () => {
      if (open && buttonRef.current) {
        setListWidth(buttonRef.current.offsetWidth)
      }
    }
    if (open) window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [open])

  // Navegação por teclado
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setOpen(true)
        requestAnimationFrame(() => {
          listRef.current?.querySelector<HTMLElement>('li[tabindex="0"]')?.focus()
        })
      }
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(prev => (prev + 1) % options.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(prev => (prev - 1 + options.length) % options.length)
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const newValue = options[activeIndex]
      onChange(newValue)
      setOpen(false)
    }
  }

  const handleSelect = (size: number) => {
    onChange(size)
    setOpen(false)
  }

  return (
    <div ref={containerRef} className="inline-flex items-center gap-2" onKeyDown={handleKeyDown}>
      {label && <span className="text-sm text-gray-600 select-none" id="page-size-label">{label}:</span>}
      <div className="relative" aria-label={label}>
        <button
          ref={buttonRef}
          type="button"
          aria-haspopup="listbox"
          {...(open ? { 'aria-expanded': 'true' } : { 'aria-expanded': 'false' })}
          aria-controls={open ? listboxId : undefined}
          className="inline-flex w-full items-center justify-between gap-2 px-6 py-1.5 rounded-full bg-white/90 backdrop-blur-sm border border-gray-300 hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-400 shadow-sm text-sm font-medium transition-colors min-w-[4.5rem]"
          onClick={() => setOpen(o => !o)}
        >
          <span className="text-gray-700">{value}</span>
          <svg className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {open && (
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            tabIndex={-1}
            aria-activedescendant={`page-size-${options[activeIndex]}`}
            aria-label="Selecione quantidade por página"
            className={`absolute left-0 z-20 mt-1 max-h-60 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg p-1 focus:outline-none animate-fade-in ${listWidth ? 'w-[--dynamic-width]' : ''}`}
            data-dynamic-width={listWidth || undefined}
          >
            {options.map((opt, idx) => {
              const active = idx === activeIndex
              const selected = opt === value
              return (
                <li
                  id={`page-size-${opt}`}
                  key={opt}
                  role="option"
                  {...(selected ? { 'aria-selected': 'true' } : {})}
                  tabIndex={0}
                  onClick={() => handleSelect(opt)}
                  onMouseEnter={() => setActiveIndex(idx)}
                  className={`cursor-pointer select-none px-3 py-1.5 rounded-lg text-sm flex items-center justify-between gap-2 transition-colors
                    ${active ? 'bg-orange-50 text-orange-600' : 'text-gray-700'}
                    ${selected ? 'font-semibold' : 'font-normal'}
                    hover:bg-orange-100 focus:bg-orange-100 focus:outline-none`}
                >
                  <span>{opt}</span>
                  {selected && (
                    <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

export default PageSizeSelect
